import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useRovingTabIndex - A hook for implementing roving tabindex pattern
 * 
 * This pattern is the recommended approach for keyboard navigation in:
 * - Tab lists (role="tablist")
 * - Menus (role="menu")
 * - Toolbars (role="toolbar")
 * - Tree views (role="tree")
 * 
 * How it works:
 * - Only one item in the group has tabIndex=0 (the focused item)
 * - All other items have tabIndex=-1
 * - Arrow keys move focus between items
 * - Home/End jump to first/last items
 * 
 * @param {Object} options
 * @param {number} options.itemCount - Total number of items
 * @param {number} options.initialIndex - Initial focused index (default: 0)
 * @param {boolean} options.vertical - Use Up/Down arrows instead of Left/Right (default: false)
 * @param {boolean} options.loop - Loop around at ends (default: true)
 * @param {Function} options.onSelect - Callback when Enter/Space is pressed
 * @returns {Object} - { focusedIndex, getTabIndex, handleKeyDown, setFocusedIndex, itemRefs }
 */
interface RovingTabIndexOptions {
  itemCount: number;
  initialIndex?: number;
  vertical?: boolean;
  loop?: boolean;
  onSelect?: (index: number, event: React.KeyboardEvent) => void;
}

export function useRovingTabIndex({
  itemCount,
  initialIndex = 0,
  vertical = false,
  loop = true,
  onSelect,
}: RovingTabIndexOptions) {
  const [focusedIndex, setFocusedIndex] = useState(initialIndex);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  // Ensure refs array is the right size
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, itemCount);
  }, [itemCount]);

  // Focus the element when focusedIndex changes
  useEffect(() => {
    const element = itemRefs.current[focusedIndex];
    if (element && document.activeElement !== element) {
      element.focus();
    }
  }, [focusedIndex]);

  const getTabIndex = useCallback((index: number) => {
    return index === focusedIndex ? 0 : -1;
  }, [focusedIndex]);

  const setItemRef = useCallback((index: number) => (el: HTMLElement | null) => {
    itemRefs.current[index] = el;
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
    const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';

    let newIndex = focusedIndex;
    let handled = false;

    switch (event.key) {
      case prevKey:
        handled = true;
        if (focusedIndex > 0) {
          newIndex = focusedIndex - 1;
        } else if (loop) {
          newIndex = itemCount - 1;
        }
        break;

      case nextKey:
        handled = true;
        if (focusedIndex < itemCount - 1) {
          newIndex = focusedIndex + 1;
        } else if (loop) {
          newIndex = 0;
        }
        break;

      case 'Home':
        handled = true;
        newIndex = 0;
        break;

      case 'End':
        handled = true;
        newIndex = itemCount - 1;
        break;

      case 'Enter':
      case ' ':
        handled = true;
        if (onSelect) {
          onSelect(focusedIndex, event);
        }
        break;

      default:
        break;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (newIndex !== focusedIndex) {
      setFocusedIndex(newIndex);
    }
  }, [focusedIndex, itemCount, vertical, loop, onSelect]);

  return {
    focusedIndex,
    setFocusedIndex,
    getTabIndex,
    handleKeyDown,
    setItemRef,
    itemRefs,
  };
}

export default useRovingTabIndex;
